// src/components/Login.js
// Componente de autenticación principal

import React, { useState, useEffect } from 'react';
import { signInWithRedirect, fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import Logo from './Logo';
import { hasAccess, ALLOWED_ROLES } from '../utils/roleUtils';

function Login({ onLoginSuccess }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState(null); // Para mostrar info de debugging

  // Función para obtener y mostrar información de grupos de Cognito
  const getCognitoGroups = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (!idToken) {
        // console.warn('⚠️ No se pudo obtener el ID token');
        return [];
      }
      
      // Obtener el payload del token (donde están los grupos)
      const payload = idToken.payload || {};
      const groups = payload["cognito:groups"] || [];
      
      // Imprimir información de debugging
      // console.log('🔍 === INFORMACIÓN DE COGNITO ===');
      // console.log('👤 Usuario:', payload.email || payload.username || 'No disponible');
      // console.log('🏷️ Grupos de Cognito:', groups);
      // console.log('📋 Payload completo del token:', payload);
      // console.log('================================');
      
      // DEBUGGING ADICIONAL: Alert temporal para asegurar que lo veas
      // if (groups.length > 0) {
      //   alert(`✅ GRUPOS DETECTADOS: ${groups.join(', ')}`);
      // } else {
      //   alert('⚠️ NO SE DETECTARON GRUPOS DE COGNITO');
      // }
      
      // Guardar info para mostrar en la UI (solo en desarrollo)
      if (process.env.NODE_ENV === 'development') {
        setDebugInfo({
          usuario: payload.email || payload.username || 'No disponible',
          grupos: groups,
          timestamp: new Date().toLocaleTimeString()
        });
      }
      
      // 🌐 GUARDAR EN VARIABLES GLOBALES para acceso desde cualquier componente
      window.COGNITO_GROUPS = groups;
      window.COGNITO_USER = payload.email || payload.username || 'No disponible';
      window.COGNITO_TIMESTAMP = new Date().toLocaleTimeString();
      window.COGNITO_FULL_PAYLOAD = payload;
      
      // console.log('🌐 Variables globales establecidas:');
      // console.log('window.COGNITO_GROUPS:', window.COGNITO_GROUPS);
      // console.log('window.COGNITO_USER:', window.COGNITO_USER);
      
      return groups;
    } catch (error) {
      // console.error('❌ [COGNITO] Error al obtener grupos de Cognito:', error);
      // NO retornar array vacío en caso de error - esto enmascaraba el problema
      throw error;
    }
  };

  // Función para obtener headers con autenticación Cognito
  const getAuthHeaders = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      
      if (!idToken) {
        throw new Error('No se pudo obtener el ID token');
      }
      
      return {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      // console.error('Error al obtener el token de autenticación:', error);
      throw error;
    }
  };

  // Función para verificar los grupos de Cognito del usuario
  const checkUserAccess = async () => {
    try {
      // console.log('🔒 [LOGIN] Iniciando verificación de acceso...');
      
      // Usar la función utilitaria para verificar acceso
      const userHasAccess = await hasAccess(ALLOWED_ROLES);
      
      if (userHasAccess) {
        // console.log('✅ [LOGIN] Usuario autorizado - acceso concedido');
        return true;
      } else {
        // console.log('❌ [LOGIN] Usuario NO autorizado - acceso denegado');
        throw new Error('No tienes permisos para acceder a esta aplicación.');
      }
    } catch (error) {
      // console.error('💥 [LOGIN] Error al verificar el acceso del usuario:', error);
      throw error;
    }
  };

  const handleLogin = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Redirigir al Hosted UI de Cognito
      await signInWithRedirect({
        provider: 'Cognito'
      });
    } catch (error) {
      // console.error('Error al iniciar sesión:', error);
      setError('Error al iniciar sesión: ' + error.message);
      setIsLoading(false);
    }
  };

  // Efecto para verificar el rol después de la autenticación
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // console.log('🔄 [LOGIN] Verificando autenticación...');
        const session = await fetchAuthSession();
        // console.log('📋 [LOGIN] Session obtenida:', !!session.tokens?.idToken);
        if (session.tokens?.idToken) {
          // console.log('🔑 [LOGIN] Token válido detectado, verificando acceso...');
          
          // ✨ VERIFICAR ACCESO DIRECTAMENTE (que incluye obtener grupos)
          const hasAccess = await checkUserAccess();
          if (hasAccess) {
            // console.log('✅ [LOGIN] Acceso concedido, redirigiendo...');
            onLoginSuccess();
          } else {
            // console.log('❌ [LOGIN] Acceso denegado');
          }
        }
      } catch (error) {
        // console.error('Error en la verificación de autenticación:', error);
        setError(error.message);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [onLoginSuccess]);

  return (
    <div className="login-container animate-fade-in">
      <div className="login-card card">
        <div className="login-header">
          <Logo variant="full" size="large" />
          <h2 className="card-title">Iniciar Sesión</h2>
          <p className="login-subtitle">Usa tus credenciales para acceder al sistema de forma segura</p>
        </div>
        
        <div className="auth-info">
          <p className="auth-description">
            🔐 Autenticación segura
          </p>
          <p className="auth-note">
            Serás redirigido a una página segura para iniciar sesión
          </p>
        </div>
        
        <button 
          onClick={handleLogin} 
          className={`btn btn-primary ${isLoading ? 'btn-loading' : ''}`}
          disabled={isLoading}
        >
          {isLoading ? 'Redirigiendo...' : 'Iniciar Sesión'}
        </button>
        
        {/* Botón de debug para probar grupos (solo visible en desarrollo) */}
        {process.env.NODE_ENV === 'development' && (
          <button 
                         onClick={async () => {
               await getCognitoGroups();
             }}
            className="btn btn-outline"
            style={{ marginTop: '10px' }}
          >
            🧪 Test Grupos (Debug)
          </button>
        )}
        
        {error && <div className="message message-error">{error}</div>}
        
        {/* Información de debugging de grupos (solo en desarrollo) */}
        {process.env.NODE_ENV === 'development' && debugInfo && (
          <div className="debug-info">
            <h4>🔍 Debug Info - Grupos de Cognito</h4>
            <p><strong>👤 Usuario:</strong> {debugInfo.usuario}</p>
            <p><strong>🏷️ Grupos:</strong> {debugInfo.grupos.length > 0 ? debugInfo.grupos.join(', ') : 'Ninguno'}</p>
            <p><strong>⏰ Último check:</strong> {debugInfo.timestamp}</p>
          </div>
        )}
      </div>

      <style jsx>{`
        .login-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          padding: 20px;
          background-color: #f8f9fa;
        }
        .login-card {
          width: 100%;
          max-width: 400px;
          padding: 2rem;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .login-header {
          text-align: center;
          margin-bottom: 2rem;
        }
        .card-title {
          margin: 1rem 0;
          color: #333;
        }
        .login-subtitle {
          color: #666;
          margin: 0;
        }
        .auth-info {
          margin: 1.5rem 0;
          padding: 1rem;
          background-color: #f8f9fa;
          border-radius: 6px;
        }
        .auth-description {
          margin: 0 0 0.5rem;
          color: #333;
          font-weight: 500;
        }
        .auth-note {
          margin: 0.5rem 0;
          color: #666;
          font-size: 0.9rem;
        }
        .auth-warning {
          margin: 0.5rem 0 0;
          color: #856404;
          background-color: #fff3cd;
          padding: 0.5rem;
          border-radius: 4px;
          font-size: 0.9rem;
        }
        .message {
          margin-top: 1rem;
          padding: 0.75rem;
          border-radius: 4px;
          text-align: center;
        }
        .message-error {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }
        .debug-info {
          margin-top: 1rem;
          padding: 1rem;
          background-color: #e7f3ff;
          border: 1px solid #b3d9ff;
          border-radius: 6px;
          text-align: left;
        }
        .debug-info h4 {
          margin: 0 0 0.5rem 0;
          color: #0066cc;
        }
        .debug-info p {
          margin: 0.25rem 0;
          font-size: 0.9rem;
        }
        .btn {
          width: 100%;
          padding: 0.75rem;
          font-size: 1rem;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .btn-primary {
          background-color: #0066cc;
          color: white;
          border: none;
        }
        .btn-primary:hover {
          background-color: #0052a3;
        }
        .btn-primary:disabled {
          background-color: #ccc;
          cursor: not-allowed;
        }
        .btn-loading {
          position: relative;
          color: transparent;
        }
        .btn-loading::after {
          content: '';
          position: absolute;
          width: 20px;
          height: 20px;
          top: 50%;
          left: 50%;
          margin: -10px 0 0 -10px;
          border: 2px solid #fff;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default Login;
